import isImageGalleryItemCurrentImagePreview from '../utils/isImageGalleryItemCurrentImagePreview';
import { observer } from 'mobx-react';
import { observer as observerLite } from 'mobx-react-lite';
import { IconButton } from '@fluentui/react/lib/Button';
import { IImageProps, Image, ImageFit } from '@fluentui/react/lib/Image';
import { ControlIcons } from 'owa-control-icons';
import { ImageGalleryItem, ImagePreviewState } from 'owa-sxsdata';
import * as React from 'react';

export interface ImagePreviewFilmStripProps {
    setPreviewToGalleryItem: (
        imageGalleryItem: ImageGalleryItem,
        triggeredBy: string
    ) => Promise<void>;
    viewState: ImagePreviewState;
}

export interface ImagePreviewFilmStripState {
    leftArrowHidden: boolean;
    rightArrowHidden: boolean;
    scrollPosition: number;
}

const styles = require('./ImagePreviewFilmStrip.scss');
const classNames = require('classnames/bind').bind(styles);
const totalWidthOfImageItem = 164;

@observer
export default class ImagePreviewFilmStrip extends React.Component<
    ImagePreviewFilmStripProps,
    ImagePreviewFilmStripState
> {
    private scrollRegion: HTMLDivElement;
    private previousPreviewUrl: string;
    private previousId: any;
    private updateArrowVisibilityTimeoutHandle: NodeJS.Timer;
    private componentDidUpdateTimeoutHandle: NodeJS.Timer;

    constructor(props: ImagePreviewFilmStripProps) {
        super(props);

        this.state = {
            leftArrowHidden: true,
            rightArrowHidden: true,
            scrollPosition: 0,
        };
    }

    render() {
        const imageGallery = this.props.viewState.imageGallery;
        const mainClassNames = classNames(
            styles.main,
            imageGallery && imageGallery.length > 1 && styles.show
        );
        const leftArrowContainerClassNames = styles.leftArrowContainer;
        const leftArrowClassNames = classNames(
            styles.arrowButton,
            this.state.leftArrowHidden && styles.hidden
        );
        const rightArrowContainerClassNames = styles.rightArrowContainer;
        const rightArrowClassNames = classNames(
            styles.arrowButton,
            this.state.rightArrowHidden && styles.hidden
        );
        const scrollAreaInnerStyles = {
            transform: `translate3d(-${this.state.scrollPosition}px,0px,0px)`,
        };

        return (
            <div className={mainClassNames}>
                <div className={styles.filmStrip}>
                    <IconButton
                        className={leftArrowContainerClassNames}
                        onClick={this.onLeftButtonClicked}
                        iconProps={{
                            iconName: ControlIcons.CaretLeftSolid8,
                            styles: {
                                root: leftArrowClassNames,
                            },
                        }}
                        styles={{
                            root: styles.arrowButtonContainer,
                        }}
                        tabIndex={-1}
                    />
                    <IconButton
                        className={rightArrowContainerClassNames}
                        onClick={this.onRightButtonClicked}
                        iconProps={{
                            iconName: ControlIcons.CaretRightSolid8,
                            styles: {
                                root: rightArrowClassNames,
                            },
                        }}
                        styles={{
                            root: styles.arrowButtonContainer,
                        }}
                        tabIndex={-1}
                    />
                    <div className={styles.scrollArea} ref={this.onScrollRegionElementUpdated}>
                        <div style={scrollAreaInnerStyles}>
                            {imageGallery &&
                                imageGallery.map((imageGalleryItem, index) => (
                                    <FilmStripImage
                                        key={'imageGalleryItem' + index}
                                        src={imageGalleryItem.thumbnailUrl}
                                        width={160}
                                        height={120}
                                        className={classNames(
                                            styles.thumbnail,
                                            isImageGalleryItemCurrentImagePreview(
                                                imageGalleryItem,
                                                this.props.viewState
                                            ) && styles.selected
                                        )}
                                        imageFit={ImageFit.cover}
                                        setPreviewToGalleryItem={this.setPreviewToGalleryItem}
                                        imageGalleryItem={imageGalleryItem}
                                    />
                                ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    //tslint:disable-next-line:react-strict-mode  Tracked by WI 78461
    UNSAFE_componentWillUpdate(
        nextProps: ImagePreviewFilmStripProps,
        nextState: ImagePreviewFilmStripState
    ) {
        const imageGallery = this.props.viewState.imageGallery;
        const nextImageGallery = nextProps.viewState.imageGallery;
        if (
            (!imageGallery && !!nextImageGallery) ||
            (!!imageGallery &&
                !!nextImageGallery &&
                imageGallery.length !== nextImageGallery.length) ||
            this.props.viewState.id !== nextProps.viewState.id ||
            this.props.viewState.previewUrl !== nextProps.viewState.previewUrl
        ) {
            this.delayedUpdateArrowVisibility();
        }
    }

    componentDidUpdate() {
        // a 50ms delay to prevent changing setScrollPosition too often
        // and making the filmstrip jumping around
        if (this.componentDidUpdateTimeoutHandle) {
            clearTimeout(this.componentDidUpdateTimeoutHandle);
        }

        this.componentDidUpdateTimeoutHandle = setTimeout(() => {
            this.componentDidUpdateTimeoutHandle = null;
            const imageGallery = this.props.viewState.imageGallery;
            if (
                this.scrollRegion &&
                imageGallery &&
                (this.previousId !== this.props.viewState.id ||
                    this.previousPreviewUrl !== this.props.viewState.previewUrl)
            ) {
                imageGallery.forEach((imageGalleryItem: ImageGalleryItem, index: number) => {
                    if (
                        isImageGalleryItemCurrentImagePreview(
                            imageGalleryItem,
                            this.props.viewState
                        )
                    ) {
                        const leftPositionOfThumbnail = index * totalWidthOfImageItem;
                        const rightPositionOfThumbnail =
                            leftPositionOfThumbnail + totalWidthOfImageItem;
                        const scrollRegionWidth = this.scrollRegion.clientWidth;
                        const leftPositionOfScrollRegion = this.state.scrollPosition;
                        const rightPositionOfScrollRegion =
                            leftPositionOfScrollRegion + scrollRegionWidth;

                        if (leftPositionOfScrollRegion > leftPositionOfThumbnail) {
                            // the thumbnail is not fully visible on the left hand side
                            this.setScrollPosition(leftPositionOfThumbnail);
                        } else if (rightPositionOfScrollRegion < rightPositionOfThumbnail) {
                            // the thumbnail is not fully visible on the right hand side
                            this.setScrollPosition(rightPositionOfThumbnail - scrollRegionWidth);
                        }
                    }
                }, this);
            }

            this.previousId = this.props.viewState.id;
            this.previousPreviewUrl = this.props.viewState.previewUrl;
        }, 50);
    }

    componentDidMount() {
        document.addEventListener('fullscreenchange', this.onResize); // Standard
        document.addEventListener('webkitfullscreenchange', this.onResize); // Chrome
        document.addEventListener('mozfullscreenchange', this.onResize); // Firefox
        document.addEventListener('MSFullscreenChange', this.onResize); // IE11
        document.addEventListener('fullscreenChange', this.onResize); // Edge
    }

    componentWillUnmount() {
        document.removeEventListener('fullscreenchange', this.onResize); // Standard
        document.removeEventListener('webkitfullscreenchange', this.onResize); // Chrome
        document.removeEventListener('mozfullscreenchange', this.onResize); // Firefox
        document.removeEventListener('MSFullscreenChange', this.onResize); // IE11
        document.removeEventListener('fullscreenChange', this.onResize); // Edge
    }

    private onScrollRegionElementUpdated = (newScrollRegion: HTMLDivElement) => {
        this.scrollRegion = newScrollRegion;
        this.delayedUpdateArrowVisibility();
    };

    onResize = () => {
        this.delayedUpdateArrowVisibility();
    };

    private delayedUpdateArrowVisibility = () => {
        if (this.updateArrowVisibilityTimeoutHandle) {
            clearTimeout(this.updateArrowVisibilityTimeoutHandle);
        }

        this.updateArrowVisibilityTimeoutHandle = setTimeout(() => {
            this.updateArrowVisibility();
        }, 100);
    };

    private updateArrowVisibility = (position: number = this.state.scrollPosition) => {
        this.updateArrowVisibilityTimeoutHandle = null;
        let hideLeftArrow = true;
        let hideRightArrow = true;

        if (this.scrollRegion) {
            hideLeftArrow = false;
            hideRightArrow = false;
            const imageGallery = this.props.viewState.imageGallery;

            // fix position if it is out of range
            if (position < 0) {
                position = 0;
            } else {
                const widthOfAllElements = imageGallery?.length * totalWidthOfImageItem;
                if (widthOfAllElements < this.scrollRegion.clientWidth) {
                    position = 0;
                } else if (position + this.scrollRegion.clientWidth > widthOfAllElements) {
                    position = widthOfAllElements - this.scrollRegion.clientWidth;
                }
            }

            // If the scroll position is at zero that means it's already as far left as it will go, so hide the left arrow
            if (position === 0) {
                hideLeftArrow = true;
            }

            // Calculate how much area is needed to display all of the elements, and then check to see if the visible area includes
            // the end of the list.  ClientWidth is the visible area of the scroll region, so position + ClientWidth gives us the largest
            // point on the x-axis that is visible.
            const widthOfAllElements = imageGallery?.length * totalWidthOfImageItem;
            if (position + this.scrollRegion.clientWidth >= widthOfAllElements) {
                hideRightArrow = true;
            }
        }

        if (
            this.state.leftArrowHidden !== hideLeftArrow ||
            this.state.rightArrowHidden !== hideRightArrow ||
            this.state.scrollPosition !== position
        ) {
            this.setState({
                leftArrowHidden: hideLeftArrow,
                rightArrowHidden: hideRightArrow,
                scrollPosition: position,
            });
        }
    };

    private onLeftButtonClicked = () => {
        this.setScrollPosition(this.state.scrollPosition - this.GetDistanceToScroll());
    };

    private onRightButtonClicked = () => {
        this.setScrollPosition(this.state.scrollPosition + this.GetDistanceToScroll());
    };

    private setScrollPosition(position: number): void {
        if (position !== this.state.scrollPosition) {
            this.updateArrowVisibility(position);
        }
    }

    private setPreviewToGalleryItem = (imageGalleryItem: ImageGalleryItem, triggeredBy: string) =>
        this.props.setPreviewToGalleryItem(imageGalleryItem, triggeredBy);

    private GetDistanceToScroll(): number {
        return this.scrollRegion.clientWidth - totalWidthOfImageItem;
    }
}
interface FilmStripImageProps extends IImageProps {
    imageGalleryItem: ImageGalleryItem;
    setPreviewToGalleryItem: (
        imageGalleryItem: ImageGalleryItem,
        triggeredBy: string
    ) => Promise<void>;
}

const FilmStripImage = observerLite(function FilmStripImage(props: FilmStripImageProps) {
    const onClickCallback = React.useCallback(() => {
        props.setPreviewToGalleryItem(props.imageGalleryItem, 'filmStripClick');
    }, [props.imageGalleryItem, props.setPreviewToGalleryItem]);
    return <Image {...props} onClick={onClickCallback} />;
});
